import {
  GlobalServicesLoginDetails,
  Session,
} from '@maximizer/core/shared/domain';
import {
  InsightsLogDetails,
  OutlookInsightsEvents,
  logCustomEventDetailsToInsights,
} from './insights.util';
import { InsightsService } from '@maximizer/core/shared/insights';
import { AppDirectoryRedirectParams } from '../models';
import {
  DataCenterId,
  buildApiWebRequest,
  getDecodedAccessToken,
  mapDataCenterFromToken,
} from '@maximizer/shared/util';

function getDataCenterIdOrLog(
  token: string,
  insightsService: InsightsService,
): DataCenterId | undefined {
  const dataCenterId = mapDataCenterFromToken(token);

  if (!dataCenterId) {
    console.error('Could not resolve dataCenterId');

    const event: InsightsLogDetails = {
      insights: insightsService,
      name: 'App-Store-Util-Missing-DataCenterId',
      eventId: OutlookInsightsEvents.AppStoreRequest,
      token: getDecodedAccessToken(token),
      error: 'Could not resolve dataCenterId',
    };

    logCustomEventDetailsToInsights(event);

    return undefined;
  }

  return dataCenterId;
}

function serializeParams(
  params: AppDirectoryRedirectParams,
  token: string,
  install: boolean,
  onMissingParams?: (missingParams: string[], errorMessage: string, token: string) => void,
): string | undefined {
  const normalizedParams = { ...params };

  if (install) {
    delete normalizedParams.maxToken;
  }

  const missingParams = Object.entries(normalizedParams)
    .filter(([, value]) => value === null || value === undefined || value === '')
    .map(([key]) => key);

  if (missingParams.length > 0) {
    const errorMessage = `Missing parameters for App Store request: ${JSON.stringify(missingParams)}`;
    console.error(errorMessage);
    onMissingParams?.(missingParams, errorMessage, token);

    return undefined;
  }

  return JSON.stringify(normalizedParams);
}

export function constructForm(
  token: string,
  params: string,
  url: string,
  insightsService: InsightsService,
): {
  form: HTMLFormElement;
  window: Window;
} | null {
  const newWindow = window.open('', '_blank');

  if (!newWindow) {
    const event: InsightsLogDetails = {
      insights: insightsService,
      name: 'App-Store-Util-Open-Window-Fail',
      eventId: OutlookInsightsEvents.AppStoreRequest,
      token: getDecodedAccessToken(token),
      alias: undefined,
      error: 'Failed to open app store in new window.',
    };

    logCustomEventDetailsToInsights(event);
    console.error('Failed to open app store in new window.');

    return null;
  }

  const appDirectoryForm = newWindow.document.createElement('form');
  appDirectoryForm.method = 'POST';
  appDirectoryForm.action = url;

  try {
    const dataParametersInput = newWindow.document.createElement('input');
    dataParametersInput.type = 'hidden';
    dataParametersInput.name = 'DataParameters';
    dataParametersInput.value = params;

    appDirectoryForm.appendChild(dataParametersInput);
    newWindow.document.body.appendChild(appDirectoryForm);
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown Error';

    const event: InsightsLogDetails = {
      insights: insightsService,
      name: 'App-Store-Util-Form-Construct-Fail',
      eventId: OutlookInsightsEvents.AppStoreRequest,
      token: getDecodedAccessToken(token),
      alias: undefined,
      error: errorMessage,
    };

    logCustomEventDetailsToInsights(event);
    console.error('Error constructing form:', errorMessage);

    return null;
  }

  return { form: appDirectoryForm, window: newWindow };
}

export function constructAppDirectoryParamsCore(
  session: Session,
  token: string,
  install: boolean,
  insightsService: InsightsService,
): string | undefined {
  const dataCenterId = getDataCenterIdOrLog(token, insightsService);

  if (!dataCenterId) {
    return undefined;
  }

  const params: AppDirectoryRedirectParams = {
    webApiUrlRequest: buildApiWebRequest(dataCenterId, session.alias),
    userId: session.user.id,
    alias: session.alias,
    dataCenterId,
    customerId: session.customerId,
    tenantPlatform: 0,
    tenantName: session.displayName,
    tenantKey: session.database,
    workspace: session.workspace,
    maxToken: token,
  };

  return serializeParams(params, token, install);
}

export function constructAppDirectoryParams(
  session: Session,
  alias: string,
  token: string,
  tenantId: string,
  logInDetails: GlobalServicesLoginDetails,
  install: boolean,
  insightsService: InsightsService,
): string | undefined {
  const dataCenterId = getDataCenterIdOrLog(token, insightsService);

  if (!dataCenterId) {
    return undefined;
  }

  const params: AppDirectoryRedirectParams = {
    webApiUrlRequest: buildApiWebRequest(dataCenterId, alias),
    userId: session.user.id,
    alias,
    dataCenterId,
    customerId: tenantId ?? '',
    tenantPlatform: 0,
    tenantName: logInDetails?.tenant.name ?? '',
    tenantKey: session.database,
    workspace: session.workspace,
    maxToken: token,
  };

  return serializeParams(params, token, install, (missingParams, errorMessage, missingParamsToken) => {
    const event: InsightsLogDetails = {
      insights: insightsService,
      name: 'App-Store-Util-Missing-Parameters',
      eventId: OutlookInsightsEvents.AppStoreRequest,
      token: getDecodedAccessToken(missingParamsToken),
      alias,
      error: errorMessage,
      metadata: { missingParams: JSON.stringify(missingParams) },
    };

    logCustomEventDetailsToInsights(event);
  });
}
